// Local UI preferences. Global ones (layout, wrap, explorer mode) and per-PR
// ones (folds, cursor per file, closed folders, unfolded commit runs) live
// in localStorage; they are conveniences, so storage failures are ignored. Per-PR state names
// repositories and paths, so it belongs to the signed-in user: it is deleted
// at sign-out and when another user signs in on this browser profile, and
// only the most recently visited MAX_PR_PREFS pull requests keep theirs.
import { FILTER_KEY } from '../logic/dashFilter';
import { SECTIONS_KEY } from '../logic/sections';
import { PLACES_PREFIX } from '../jump/places.svelte';
import { DEFAULT_ALGORITHM } from '../diff/algorithms';
import type { DiffAlgorithm } from '../engine/types';
import type { Theme } from '../theme';
import { subjectKey, type Subject } from '../subject';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && !Array.isArray(v) && fallback && typeof fallback === 'object'
      ? { ...fallback, ...v }
      : (v as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

export interface GlobalPrefs {
  diffMode: 'split' | 'unified';
  /** How the Files view's diffs align lines (read through `algorithmOf`: a stored value may be stale). */
  diffAlgorithm: DiffAlgorithm;
  wrap: boolean;
  explorerView: 'tree' | 'list';
  /** Explorer lists the PR's changed files, or every file of the repository at the head. */
  explorerScope: 'changed' | 'all';
  unreviewedOnly: boolean;
  theme: Theme;
  /** The Files view's explorer column is hidden to give the diff the width. */
  explorerHidden: boolean;
  /** The Files view's commit timeline is folded to its one-line range summary. */
  timelineCollapsed: boolean;
  /** Files with a rendered form show it ('preview') or their text ('source'), per kind. */
  preview: { markdown: 'source' | 'preview'; image: 'source' | 'preview' };
  /** Dashboard sections gather stacked pull requests ('stacked') or list every row on its own ('flat'). */
  dashboardView: 'stacked' | 'flat';
  /** The Files view's file list width in pixels, or null for the width of the window's size (logic/resize.ts). */
  explorerWidth: number | null;
  /** The split diff's base column in percent of the diff's width, or null for half. */
  splitBase: number | null;
  /** The Overview's left column (outline and Links) in pixels, or null for views.css's width; likewise its right column, and the side column of two. */
  overviewLeft: number | null;
  overviewRight: number | null;
  overviewSide: number | null;
  /** The Files view's minimap: the picture of the file ('rich'), or a thin strip of its changes. */
  minimap: 'rich' | 'thin';
}

const GLOBAL_KEY = 'changelist:prefs';

/**
 * Where builds before the server kept it stored the "Viewed" sync choice:
 * this field of the global preferences, in this browser, whoever was
 * signed in. Only an explicit `false` there means anything now (see
 * state/settings/program.ts, which carries it to the server).
 */
const LEGACY_VIEWED = 'githubViewed';

class Prefs {
  value: GlobalPrefs;
  /** An earlier build's "Viewed" sync choice in this browser was off, and is still to be carried to the server. */
  #legacyViewedOff: boolean;

  constructor() {
    const stored = read<GlobalPrefs & { [LEGACY_VIEWED]?: unknown }>(GLOBAL_KEY, { diffMode: 'split', diffAlgorithm: DEFAULT_ALGORITHM, wrap: true, explorerView: 'tree', explorerScope: 'changed', unreviewedOnly: false, theme: 'auto', explorerHidden: false, timelineCollapsed: false, preview: { markdown: 'source', image: 'preview' }, dashboardView: 'stacked', explorerWidth: null, splitBase: null, overviewLeft: null, overviewRight: null, overviewSide: null, minimap: 'rich' });
    const { [LEGACY_VIEWED]: legacy, ...value } = stored;
    this.#legacyViewedOff = legacy === false;
    this.value = $state(value);
    // "On" was the default and is again: nothing to carry, so the field goes now.
    if (legacy !== undefined && legacy !== false) this.save();
  }

  /** Stored with the old "off" while it waits to be carried, so no other write here loses it. */
  private save() {
    write(GLOBAL_KEY, this.#legacyViewedOff ? { ...this.value, [LEGACY_VIEWED]: false } : this.value);
  }

  set<K extends keyof GlobalPrefs>(key: K, v: GlobalPrefs[K]) {
    this.value[key] = v;
    this.save();
  }

  /** Whether an earlier build's explicit "off" for the "Viewed" sync is still kept in this browser. */
  get legacyViewedOff(): boolean {
    return this.#legacyViewedOff;
  }

  /** Forget the earlier build's "Viewed" sync choice: the server holds the user's now. */
  dropLegacyViewed() {
    if (!this.#legacyViewedOff) return;
    this.#legacyViewedOff = false;
    this.save();
  }
}

export const prefs = new Prefs();

export interface PrPrefsData {
  folded: Record<string, boolean>;
  cursor: Record<string, string>;
  closedFolders: Record<string, boolean>;
  /** "All files" folders the user opened (true) or closed (false); others open when they hold a changed file. */
  allFolders: Record<string, boolean>;
  lastFile: string | null;
  /** The Overview's commit runs the reader unfolded, by run key (`timelineRows`). */
  openRuns: Record<string, boolean>;
  /** The reviews whose inline comments the reader unfolded (in the Overview and the Review view alike), by review node ID. */
  openReviews: Record<string, boolean>;
  /** The Review view's threads the reader unfolded, by thread node ID; absent in prefs saved before there were any. */
  openThreads?: Record<string, boolean>;
  /** The Review view's conversation filter the reader last chose; absent until they choose one. */
  conversationFilter?: 'open' | 'mine' | 'all';
}

const PR_PREFIX = 'changelist:pr:';
/** Per-PR keys, least recently visited first. */
const PR_INDEX_KEY = 'changelist:pr-index';
/** ID of the user the per-user local state belongs to. */
const OWNER_KEY = 'changelist:local-user';
/** Pull requests whose UI state is kept (the most recently visited). */
export const MAX_PR_PREFS = 200;

function storageKeys(): string[] {
  try {
    return Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter((k): k is string => k !== null);
  } catch {
    return [];
  }
}

function remove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

/** Record a visit to a pull request's UI state, dropping the least recently visited beyond MAX_PR_PREFS. */
function touchPr(key: string) {
  const raw = read<unknown>(PR_INDEX_KEY, []);
  const index = (Array.isArray(raw) ? raw : []).filter((k): k is string => typeof k === 'string' && k !== key);
  index.push(key);
  for (const old of index.splice(0, Math.max(0, index.length - MAX_PR_PREFS))) remove(old);
  write(PR_INDEX_KEY, index);
}

/** What holds per-user local state in memory, told when it is deleted (`onLocalStateCleared`). */
const clearedListeners = new Set<() => void>();

/**
 * Be told whenever the per-user local state is deleted (at sign-out, or
 * when another account claims the browser), so a copy held in memory
 * (the jump bar's places) never outlives what it mirrors. The function
 * returned stops.
 */
export function onLocalStateCleared(listener: () => void): () => void {
  clearedListeners.add(listener);
  return () => void clearedListeners.delete(listener);
}

/** Delete the signed-in user's local state: per-PR UI state, the dashboard's sections and filter, and the jump bar's recents and pins (the pinned repositories among them). */
export function clearLocalUserState() {
  for (const k of storageKeys()) if (k.startsWith(PR_PREFIX) || k.startsWith(PLACES_PREFIX)) remove(k);
  remove(PR_INDEX_KEY);
  remove(SECTIONS_KEY);
  remove(FILTER_KEY);
  remove(OWNER_KEY);
  for (const f of [...clearedListeners]) f();
}

/**
 * The per-user local state now belongs to `userId`: state left by anyone
 * else (or by a version that did not record its owner) is deleted first.
 */
export function claimLocalState(userId: number | string) {
  const id = String(userId);
  let owner: string | null = null;
  try {
    owner = localStorage.getItem(OWNER_KEY);
  } catch {
    return;
  }
  if (owner === id) return;
  clearLocalUserState();
  try {
    localStorage.setItem(OWNER_KEY, id);
  } catch {
    /* storage unavailable */
  }
}

/** Per-subject UI state (a pull request's, a compare's): folds by block key, block cursor per file, closed folders. */
export class PrPrefs {
  data: PrPrefsData = $state({ folded: {}, cursor: {}, closedFolders: {}, allFolders: {}, lastFile: null, openRuns: {}, openReviews: {} });
  private key: string;
  private timer: ReturnType<typeof setTimeout> | null = null;

  /** A pull request's (`owner`, `repo`, `number`), or any subject's: kept under its key (subject.ts `subjectKey`), a pull request's the same either way. */
  constructor(owner: string, repo: string, number: number);
  constructor(subject: Subject);
  constructor(at: string | Subject, repo?: string, number?: number) {
    this.key = `${PR_PREFIX}${typeof at === 'string' ? `${at}/${repo}#${number}` : subjectKey(at)}`.toLowerCase();
    touchPr(this.key);
    this.data = read<PrPrefsData>(this.key, { folded: {}, cursor: {}, closedFolders: {}, allFolders: {}, lastFile: null, openRuns: {}, openReviews: {} });
  }

  save() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => write(this.key, $state.snapshot(this.data)), 150);
  }

  setFold(key: string, folded: boolean) {
    this.data.folded[key] = folded;
    this.save();
  }

  /** Unfold (or fold) the Overview's commit run `key`; only unfolded runs are stored. */
  setRunOpen(key: string, open: boolean) {
    if (open) this.data.openRuns[key] = true;
    else delete this.data.openRuns[key];
    this.save();
  }

  /** Unfold (or fold) thread `id` in the Review view's conversation; only unfolded threads are stored. */
  setThreadOpen(id: string, open: boolean) {
    const threads = (this.data.openThreads ??= {});
    if (open) threads[id] = true;
    else delete threads[id];
    this.save();
  }

  /** The Review view's conversation shows `filter` from now on. */
  setConversationFilter(filter: 'open' | 'mine' | 'all') {
    this.data.conversationFilter = filter;
    this.save();
  }

  /** Unfold (or fold) review `id`'s inline comments; only unfolded reviews are stored. */
  setReviewOpen(id: string, open: boolean) {
    if (open) this.data.openReviews[id] = true;
    else delete this.data.openReviews[id];
    this.save();
  }

  /** Forget unfolded runs other than `keys` (runs a force-push rewrote, say). */
  keepRuns(keys: Iterable<string>) {
    const keep = new Set(keys);
    const stale = Object.keys(this.data.openRuns).filter((k) => !keep.has(k));
    if (!stale.length) return;
    for (const k of stale) delete this.data.openRuns[k];
    this.save();
  }

  setCursor(path: string, key: string) {
    if (this.data.cursor[path] === key) return;
    this.data.cursor[path] = key;
    this.save();
  }
}
